import React, { useEffect, useRef, useState } from 'react'
import { flushSync } from 'react-dom'
import { DateTime } from 'luxon'
import AccessTimeIcon from 'components/material-symbol/icons/AccessTime'
import { DesktopTimePicker } from '@mui/x-date-pickers'
import { useIsTablet } from 'lib/hooks'
import {
  enterTimeViewRenderers,
  mergeEnterTimePickerSlotProps,
  ENTER_TIME_FIELD_FORMAT,
} from './enter-time-view'
import { setPickerPopoverOpen } from './time-picker-popover-open-registry'

let nextFallbackRegistryId = 0

const TimePickerIcon = props => <AccessTimeIcon {...props} />

const isBlurMovingToTimePickerOpener = relatedTarget => {
  if (!(relatedTarget instanceof HTMLElement)) {
    return false
  }
  return Boolean(
    relatedTarget.closest('.MuiPickersPopper-root') ||
      relatedTarget.closest('.MuiInputAdornment-root button') ||
      relatedTarget.getAttribute('aria-label') === 'Choose time',
  )
}

const normalizePickerChangeValue = value => {
  if (value == null) return value
  if (DateTime.isDateTime(value)) return value
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return DateTime.fromJSDate(value)
  }
  return value
}

const isPublishablePickerValue = value => {
  const normalized = normalizePickerChangeValue(value)
  if (normalized == null) {
    return false
  }
  if (DateTime.isDateTime(normalized)) {
    return normalized.isValid
  }
  return true
}

const PgaMobileTimePicker = ({
  id,
  label,
  value,
  onAccept,
  onChange,
  onPickerOpenChange,
  validationErrors,
  slotProps,
  slots,
  'data-cy': dataCy,
  onOpen: onPickerOpen,
  onClose: onPickerClose,
  ...pickerRest
}) => {
  const hideOpenPicker = useIsTablet()
  const [pickerOpen, setPickerOpen] = useState(false)
  const isPickerOpenRef = useRef(false)
  const isFieldFocusedRef = useRef(false)
  const blurFlushTimeoutRef = useRef()
  const pendingValueRef = useRef(value)
  const fallbackRegistryIdRef = useRef(null)
  if (fallbackRegistryIdRef.current == null) {
    nextFallbackRegistryId += 1
    fallbackRegistryIdRef.current = `pga-mobile-time-picker-${nextFallbackRegistryId}`
  }
  const registryId = id != null ? id : fallbackRegistryIdRef.current

  useEffect(() => {
    pendingValueRef.current = value
  }, [value])

  const notifyPopoverOpen = open => {
    setPickerPopoverOpen(registryId, open)
    onPickerOpenChange?.(open)
  }

  useEffect(
    () => () => {
      if (blurFlushTimeoutRef.current != null) {
        window.clearTimeout(blurFlushTimeoutRef.current)
      }
      setPickerPopoverOpen(registryId, false)
      if (isPickerOpenRef.current) {
        isPickerOpenRef.current = false
        onPickerOpenChange?.(false)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- registryId stable per mount; notify on unmount only
    [registryId],
  )

  const publishToParent = normalized => {
    onChange?.(normalized)
  }

  const normalizePublishableValue = val => {
    if (!isPublishablePickerValue(val)) {
      return null
    }
    return normalizePickerChangeValue(val)
  }

  const flushPendingToParent = () => {
    const normalized = normalizePublishableValue(pendingValueRef.current)
    if (normalized != null) {
      publishToParent(normalized)
    }
  }

  const flushPendingToParentSync = () => {
    if (isPickerOpenRef.current) {
      return
    }
    flushSync(() => {
      flushPendingToParent()
    })
  }

  const scheduleDeferredBlurFlush = () => {
    if (blurFlushTimeoutRef.current != null) {
      window.clearTimeout(blurFlushTimeoutRef.current)
    }
    blurFlushTimeoutRef.current = window.setTimeout(() => {
      blurFlushTimeoutRef.current = undefined
      if (!isPickerOpenRef.current && !isFieldFocusedRef.current) {
        flushPendingToParentSync()
      }
    }, 0)
  }

  const shouldDeferParentUpdate = () =>
    isPickerOpenRef.current || isFieldFocusedRef.current

  return (
    <DesktopTimePicker
      minutesStep={5}
      closeOnSelect={false}
      {...pickerRest}
      id={id}
      label={label}
      value={value}
      disableOpenPicker={hideOpenPicker}
      open={hideOpenPicker ? false : pickerOpen}
      onOpen={() => {
        if (hideOpenPicker) {
          return
        }
        isPickerOpenRef.current = true
        setPickerOpen(true)
        notifyPopoverOpen(true)
        onPickerOpen?.()
      }}
      onClose={() => {
        isPickerOpenRef.current = false
        setPickerOpen(false)
        flushPendingToParentSync()
        notifyPopoverOpen(false)
        onPickerClose?.()
      }}
      onChange={(val, context) => {
        const normalized = normalizePublishableValue(val)
        if (normalized == null) {
          return
        }

        if (context?.validationError == null) {
          pendingValueRef.current = normalized
        }

        if (shouldDeferParentUpdate()) {
          return
        }

        if (context?.validationError != null) {
          return
        }

        publishToParent(normalized)
      }}
      onAccept={val => {
        const normalized = normalizePublishableValue(val)
        if (normalized == null) {
          return
        }
        pendingValueRef.current = normalized
        onAccept?.(normalized)
        publishToParent(normalized)
      }}
      views={['hours']}
      format={pickerRest.format || ENTER_TIME_FIELD_FORMAT}
      viewRenderers={enterTimeViewRenderers}
      slots={{
        openPickerIcon: TimePickerIcon,
        ...slots,
      }}
      slotProps={mergeEnterTimePickerSlotProps({
        ...slotProps,
        textField: {
          error: Boolean(validationErrors),
          ...slotProps?.textField,
          onFocus: event => {
            isFieldFocusedRef.current = true
            slotProps?.textField?.onFocus?.(event)
          },
          onBlur: event => {
            isFieldFocusedRef.current = false
            slotProps?.textField?.onBlur?.(event)
            if (isBlurMovingToTimePickerOpener(event.relatedTarget)) {
              scheduleDeferredBlurFlush()
              return
            }
            flushPendingToParentSync()
          },
          inputProps: {
            'data-cy': dataCy,
            ...slotProps?.textField?.inputProps,
          },
        },
      })}
    />
  )
}

export default PgaMobileTimePicker
